import * as React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface ControlledSelectProps extends Omit<React.ComponentProps<typeof Select>, 'value' | 'onValueChange'> {
  value?: string | null | undefined;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
}

/**
 * A wrapper around the Select component that ensures it's always controlled.
 * This prevents React warnings about switching between controlled and uncontrolled states.
 * 
 * Use this component instead of the base Select when:
 * - The value might be undefined initially
 * - The value comes from async data that starts as undefined
 * - You're using form libraries that might pass undefined values
 */
export function ControlledSelect({ 
  value, 
  onValueChange,
  children,
  ...props 
}: ControlledSelectProps) {
  // Convert undefined/null to empty string to keep component controlled
  const controlledValue = value === undefined || value === null ? '' : value;
  
  return (
    <Select 
      value={controlledValue} 
      onValueChange={onValueChange}
      {...props}
    >
      {children}
    </Select>
  );
}

// Re-export commonly used Select components for convenience
export {
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
};